/**
 * Role-Aware Sidebar Navigation
 * Dynamically renders menu items based on user role and permissions
 * Mobile-responsive with close button
 */
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { Eye, AlertCircle, X, LayoutDashboard } from 'lucide-react';

const NavItem = ({ item, isActive, onClick }) => (
  <NavLink
    to={item.href}
    data-testid={item.testId}
    onClick={onClick}
    className={cn(
      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
      'hover:bg-gray-50',
      isActive
        ? 'bg-brand-orange/10 text-brand-orange font-semibold border-l-[3px] border-brand-orange -ml-[3px] pl-[calc(0.75rem+3px)]'
        : 'text-gray-700 hover:text-brand-charcoal'
    )}
  >
    <item.icon className="h-5 w-5 flex-shrink-0" />
    <span className="flex-1 truncate">{item.name}</span>
    {item.badge && (
      <span className="bg-brand-orange text-white text-xs font-semibold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center">
        {typeof item.badge === 'number' ? item.badge : '•'}
      </span>
    )}
  </NavLink>
);

export function Sidebar({ onClose, isMobile }) {
  const location = useLocation();
  const { 
    navigation, 
    isPreviewMode, 
    previewRole, 
    effectiveRole,
    roleDisplayName,
    isAuthenticated 
  } = useAuth();

  const isActive = (href) => {
    if (href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(href);
  };

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  // If not authenticated, show minimal sidebar
  if (!isAuthenticated) {
    return (
      <aside 
        data-testid="sidebar"
        className="w-64 bg-white border-r border-gray-200 flex flex-col h-[calc(100vh-4rem)]"
      >
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-400 text-sm">Please log in</p>
        </div>
      </aside>
    );
  }

  // If no navigation items (role has no access), show empty state
  if (!navigation || navigation.length === 0) {
    return (
      <aside 
        data-testid="sidebar"
        className="w-64 bg-white border-r border-gray-200 flex flex-col h-[calc(100vh-4rem)]"
      >
        {/* Mobile Close Button */}
        {isMobile && (
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <span className="font-semibold text-gray-800">Menu</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
          <AlertCircle className="h-8 w-8 text-gray-300 mb-2" />
          <p className="text-gray-500 text-sm">No modules available</p>
          <p className="text-gray-400 text-xs mt-1">
            Contact your administrator for access
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside 
      data-testid="sidebar"
      className="w-64 bg-white border-r border-gray-200 flex flex-col h-[calc(100vh-4rem)]"
    >
      {/* Mobile Header with Close Button */}
      {isMobile && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <span className="font-semibold text-gray-800">Navigation</span>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
              Preview Mode
            </span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            Viewing as: <span className="font-medium">{roleDisplayName}</span>
          </p>
        </div>
      )}

      <ScrollArea className="flex-1">
        <nav className="p-4 space-y-6">
          {/* Dashboard - Always first */}
          <div>
            <NavLink
              to="/dashboard"
              data-testid="nav-dashboard"
              onClick={handleNavClick}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                'hover:bg-gray-50',
                isActive('/dashboard')
                  ? 'bg-brand-orange/10 text-brand-orange font-semibold border-l-[3px] border-brand-orange -ml-[3px] pl-[calc(0.75rem+3px)]'
                  : 'text-gray-700 hover:text-brand-charcoal'
              )}
            >
              <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1">Dashboard</span>
            </NavLink>
          </div>

          {/* Role-based navigation sections */}
          {navigation.map((section, index) => (
            <div key={section.section || index}>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                {section.section}
              </div>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavItem 
                    key={item.href} 
                    item={item} 
                    isActive={isActive(item.href)}
                    onClick={handleNavClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer - Current Role & System Status */}
      <div className="p-4 border-t border-gray-200 space-y-3">
        {/* Current Role Badge */}
        <div className="px-3 py-2 bg-gray-50 rounded-lg">
          <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">
            Current Role
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              variant={isPreviewMode ? "outline" : "secondary"}
              className={cn(
                "text-xs font-medium",
                isPreviewMode && "border-amber-400 text-amber-700 bg-amber-50"
              )}
            >
              {roleDisplayName}
            </Badge>
            {isPreviewMode && (
              <span className="text-[10px] text-amber-600">(Preview)</span>
            )}
          </div>
        </div>

        {/* System Status */}
        <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
          <div className="w-2 h-2 bg-spice-cardamom rounded-full animate-pulse" />
          <span className="text-xs text-gray-600">System Online</span>
        </div>
      </div>
    </aside>
  );
}
